import React, { useState, useRef, useEffect } from 'react';
import { motion, useAnimation, useMotionValue, useTransform, PanInfo } from 'framer-motion';
import { ShieldCheck, ArrowRight } from 'lucide-react';
import './SlideToVerify.scss';

interface SlideToVerifyProps {
  onVerify: () => void;
  disabled?: boolean;
}

const SlideToVerify: React.FC<SlideToVerifyProps> = ({ onVerify, disabled }) => {
  const [isVerified, setIsVerified] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  
  const x = useMotionValue(0);
  const dragControls = useAnimation();
  
  const backgroundWidth = useTransform(x, [0, containerWidth - 50], ["0%", "100%"]);
  const opacityText = useTransform(x, [0, (containerWidth - 50) * 0.5], [1, 0]);

  useEffect(() => {
    if (containerRef.current) {
      setContainerWidth(containerRef.current.offsetWidth);
    }
    const handleResize = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleDragEnd = (event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    if (isVerified || disabled) return;
    
    if (info.offset.x > (containerWidth - 60) * 0.8) {
      setIsVerified(true);
      x.set(containerWidth - 50);
      onVerify();
    } else {
      dragControls.start({ x: 0, transition: { type: "spring", stiffness: 300, damping: 20 } });
      x.set(0);
    }
  };

  return (
    <div 
      className={`slide-verify ${isVerified ? 'slide-verify--verified' : ''} ${disabled ? 'slide-verify--disabled' : ''}`}
      ref={containerRef}
    >
      <motion.div 
        className="slide-verify__bg" 
        style={{ width: backgroundWidth }} 
      />
      
      <motion.span 
        className="slide-verify__text"
        style={{ opacity: isVerified ? 0 : opacityText }}
      >
        Slide to verify
      </motion.span>
      
      {isVerified && (
        <motion.span 
          className="slide-verify__success-text"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
        >
          Verified
        </motion.span>
      )}

      <motion.div
        className="slide-verify__handle"
        drag={isVerified || disabled ? false : "x"}
        dragConstraints={{ left: 0, right: containerWidth - 50 }}
        dragElastic={0.05}
        dragMomentum={false}
        onDragEnd={handleDragEnd}
        animate={dragControls}
        style={{ x }}
        whileHover={!isVerified && !disabled ? { scale: 1.05 } : {}}
        whileTap={!isVerified && !disabled ? { scale: 0.95 } : {}}
      >
        <AnimateIcon isVerified={isVerified} />
      </motion.div>
    </div>
  );
};

const AnimateIcon = ({ isVerified }: { isVerified: boolean }) => {
  return (
    <motion.div
      initial={false}
      animate={{ rotate: isVerified ? 360 : 0 }}
      transition={{ type: "spring", stiffness: 200, damping: 20 }}
    >
      {isVerified ? <ShieldCheck size={20} strokeWidth={2} /> : <ArrowRight size={20} strokeWidth={2} />}
    </motion.div>
  );
};

export default SlideToVerify;
